import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ErrorState } from "@/components/common/ErrorState";
import { StatsGrid } from "@/components/progress/StatsGrid";
import { WeeklyRecapCard } from "@/components/progress/WeeklyRecapCard";
import { RecentActivityList } from "@/components/progress/RecentActivityList";
import { useProfile } from "@/context/ProfileContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { useStats } from "@/hooks/useStats";
import { deleteActivityApi } from "@/lib/api";
import { fadeUp } from "@/lib/motion";

export default function ProgressPage() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { t } = useLanguage();
  const { stats, loading, error, reload } = useStats();

  const handleDelete = async (activityId) => {
    try {
      await deleteActivityApi(activityId, profile.id);
      toast.success(t("progress.deleted"));
      reload();
    } catch {
      toast.error(t("progress.deleteError"));
    }
  };

  // Stage 2.2: open the dedicated edit screen for an activity
  const handleEdit = (activity) => navigate(`/edit-activity/${activity.id}`);

  if (error) {
    return (
      <ErrorState
        message={t("progress.error")}
        onRetry={reload}
        testId="progress-retry-button"
      />
    );
  }

  return (
    <div className="space-y-4">
      <motion.header {...fadeUp(0)} className="pt-2">
        <p className="text-xs uppercase tracking-[0.22em] text-cyan-300/70 font-medium">
          {t("progress.kicker")}
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          {t("progress.title")}
        </h1>
      </motion.header>

      <motion.div {...fadeUp(0.06)}>
        <StatsGrid stats={stats} loading={loading} />
      </motion.div>

      <motion.div {...fadeUp(0.1)}>
        <WeeklyRecapCard stats={stats} loading={loading} />
      </motion.div>

      <motion.div {...fadeUp(0.12)}>
        <p className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-3 mt-2">
          {t("progress.recent")}
        </p>
        <RecentActivityList
          activities={stats?.recent_activities || []}
          loading={loading}
          onDelete={handleDelete}
          onEdit={handleEdit}
          onAdd={() => navigate("/add")}
        />
      </motion.div>
    </div>
  );
}
